import { GridApi } from "ag-grid-community/dist/lib/gridApi";
import { ColumnApi } from "ag-grid-community";
import { OrderedSet } from "immutable";
import _ from "lodash";
import { IGridEntry } from "types/cart/shipment-review";
import { IGridItem, getQuantityWithUnitsPerPack } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { IProps as SizingViewProps } from "../SizingViewGrid";
import { RowNode } from "ag-grid-community/dist/lib/entities/rowNode";
import { isAllocated } from "helpers/legacy-cart/bulk";
import { gridAggFunctions } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { AggFuncs } from "constants/product-collection";
import { IAggFuncParams } from "ag-grid-community/dist/lib/entities/colDef";

export const selectRows = (api: GridApi, selectedRowIds: SizingViewProps["selectedRowIds"], rowCount: number) => {
    if (!api || !rowCount) {
        return;
    }
    switch (selectedRowIds?.size) {
        case 0: {
            api.deselectAll();
            break;
        }
        case rowCount: {
            api.selectAll();
            break;
        }
        default: {
            api.forEachLeafNode((node) => node.setSelected(selectedRowIds.includes(getRowId(node))));
        }
    }
};
// expandAll() and collapseAll() don't work in current version of ag-grid. But should be fixed in future versions
export const expandRows = (api: GridApi, rowIds: SizingViewProps["expandedRowIds"]) =>
    api?.forEachLeafNode((node) => node.setExpanded(rowIds.includes(getRowId(node))));

export const getRowId = ({ data }: { data: IGridItem }): string => data?.rowId;

export const handleRowGroupOpened = (gridApi: GridApi, onRowExpandChange: SizingViewProps["onRowExpandChange"]) => {
    let allExpanded = true;
    let allCollapsed = true;
    const expandedRows: string[] = [];

    gridApi?.forEachLeafNode((node) => {
        if (node.expanded) {
            allCollapsed = false;
            const id = getRowId(node);
            expandedRows.push(id);
        } else {
            allExpanded = false;
        }
    });
    onRowExpandChange(OrderedSet(expandedRows), allExpanded, allCollapsed);
};

export const isGridColumnApiReady = (gridColumnApi: ColumnApi) => !_.isEmpty(gridColumnApi?.getAllColumns());

type Totals = {
    products: number;
    units: number;
    retail: number;
    wholesale: number;
};
export const getTotals = (entries: IGridEntry[]): Totals => {
    let totalUnits = 0;
    let totalRetail = 0;
    let totalWholesale = 0;
    const totalProducts = new Set();
    _.forEach(_.groupBy(entries, "cartLineItemId"), (group) => {
        const { units = 0, sized = 0, wholesale = 0, retail = 0, unitsPerPack = 0 } = group?.[0]?.bulkUnits ?? {};
        if (units && !sized) {
            totalUnits += getQuantityWithUnitsPerPack(units, unitsPerPack);
            totalWholesale += wholesale * getQuantityWithUnitsPerPack(units, unitsPerPack);
            totalRetail += retail * getQuantityWithUnitsPerPack(units, unitsPerPack);
            totalProducts.add(group?.[0]?.product?._id);
            return;
        }

        group?.forEach(({ cartLineItemId, quantity, unitsPerPack, product, pricing, allocated, location }) => {
            if (!cartLineItemId || !isAllocated(allocated, location)) {
                return;
            }
            const quantityWithPacks = getQuantityWithUnitsPerPack(quantity, unitsPerPack);
            const wholesale = pricing.discounted_price ?? pricing.wholesale;
            const retail = pricing.retail;

            totalUnits += quantityWithPacks;
            totalRetail += retail * quantityWithPacks;
            totalWholesale += wholesale * quantityWithPacks;
            totalProducts.add(product?._id);
        });
    });

    return {
        products: totalProducts.size,
        units: totalUnits,
        retail: totalRetail,
        wholesale: totalWholesale,
    };
};

export const getFilteredTotals = (api: GridApi): Totals => {
    const filteredEntries = [];
    api.forEachNodeAfterFilter(
        ({ data, group }: CastProp<RowNode, "data", IGridItem>) =>
            !group &&
            filteredEntries.push(...data.entries.filter(({ location, allocated }) => isAllocated(allocated, location)))
    );
    return getTotals(filteredEntries);
};

export const aggFuncs = {
    ...gridAggFunctions,
    // due to bugs in AgGrid, `allChildrenCount` could be 0 when changing the row grouping. Calculating the data manually then
    [AggFuncs.COUNT.toLocaleLowerCase()]: (params: IAggFuncParams) =>
        params.rowNode.allChildrenCount || _.size(getFilteredLeafs(params.rowNode)),
    [AggFuncs.COUNT_WITH_PERCENT.toLocaleLowerCase()]: (params: IAggFuncParams) =>
        params.rowNode.allChildrenCount || _.size(getFilteredLeafs(params.rowNode)),
};

export const isAllLocationCombinedGroup = (
    node: CastProp<RowNode, "allLeafChildren", CastProp<RowNode, "data", IGridItem>[]>
) => node.allLeafChildren[0]?.data?.location.id === null;

export const getFilteredLeafs = (node: RowNode): RowNode[] =>
    node?.group ? _.flatMap(node.childrenAfterFilter, getFilteredLeafs) : [node];

export const getPivotSettings = (columnApi: ColumnApi) => ({
    columns: _.map(columnApi.getPivotColumns(), "colId"),
    mode: columnApi.isPivotMode(),
});
